import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/articles';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setSearchOpen(false);
      setSearchQuery('');
      // 简单搜索：跳转到包含关键词的分类页
      navigate(`/?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'navbar-scrolled' : ''}`}>
      <div className="container-custom">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-chat-smile-2-fill text-lg"></i>
            </div>
            <div>
              <span className="text-lg md:text-xl font-heading font-bold text-foreground-900 group-hover:text-primary-600 transition-colors">
                黑料天天动漫
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`whitespace-nowrap px-4 py-2 text-sm font-medium rounded-full transition-colors ${isActive('/') && !location.search ? 'bg-primary-500 text-background-50' : 'text-foreground-700 hover:text-primary-600 hover:bg-primary-50'}`}
            >
              首页
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`whitespace-nowrap px-4 py-2 text-sm font-medium rounded-full transition-colors ${isActive(`/category/${cat.slug}`) ? 'bg-primary-500 text-background-50' : 'text-foreground-700 hover:text-primary-600 hover:bg-primary-50'}`}
              >
                {cat.name}
              </Link>
            ))}
            <Link
              to="/about"
              className={`whitespace-nowrap px-4 py-2 text-sm font-medium rounded-full transition-colors ${isActive('/about') ? 'bg-primary-500 text-background-50' : 'text-foreground-700 hover:text-primary-600 hover:bg-primary-50'}`}
            >
              关于我们
            </Link>
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Search */}
            <div ref={searchRef} className="relative">
              {searchOpen ? (
                <form onSubmit={handleSearch} className="flex items-center">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="搜索爆料..."
                    className="search-input w-40 md:w-52 h-9 px-3 py-1 text-sm bg-background-100 border border-secondary-200 rounded-full text-foreground-900 placeholder-foreground-400"
                    autoFocus
                  />
                  <button type="submit" className="ml-2 w-9 h-9 flex items-center justify-center rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer">
                    <i className="ri-search-line text-sm"></i>
                  </button>
                </form>
              ) : (
                <button
                  onClick={() => setSearchOpen(true)}
                  className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-600 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
                  aria-label="搜索"
                >
                  <i className="ri-search-line text-lg"></i>
                </button>
              )}
            </div>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-full text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer"
              aria-label="菜单"
            >
              <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 shadow-lg">
          <nav className="container-custom py-4 flex flex-col gap-1">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className={`px-4 py-3 rounded-lg text-sm font-medium ${isActive('/') && !location.search ? 'bg-primary-50 text-primary-600' : 'text-foreground-700 hover:bg-background-100'}`}
            >
              首页
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => setMobileMenuOpen(false)}
                className={`px-4 py-3 rounded-lg text-sm font-medium ${isActive(`/category/${cat.slug}`) ? 'bg-primary-50 text-primary-600' : 'text-foreground-700 hover:bg-background-100'}`}
              >
                {cat.name}
              </Link>
            ))}
            <Link
              to="/about"
              onClick={() => setMobileMenuOpen(false)}
              className={`px-4 py-3 rounded-lg text-sm font-medium ${isActive('/about') ? 'bg-primary-50 text-primary-600' : 'text-foreground-700 hover:bg-background-100'}`}
            >
              关于我们
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}